import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { Pagination } from '@/components/organisms/pagination';
import { BillingGroup } from '@/features/billing-groups/api/types/billing-group';
import { useBillingGroups } from '@/features/billing-groups/hooks/query/use-billing-groups';
import { usePagination } from '@/hooks/use-pagination';

interface Props extends DialogActionsInterface {
  selectedSearchGroup: BillingGroup | null;
  handleFilterByBillingGroup: (selectedGroup: BillingGroup) => void;
}

export const FilterHostsByBillingGroupDialog: FC<Props> = ({
  onCancel,
  open,
  selectedSearchGroup,
  handleFilterByBillingGroup,
}) => {
  const { t } = useTranslation(['host', 'common']);

  const [selectedGroup, setSelectedGroup] = useState<BillingGroup | null>(null);

  const { changePage, changePerPage, page, perPage, setTotalItems, totalPages } = usePagination();

  const { data, isLoading, error, isFetching } = useBillingGroups({ page, perPage });

  useEffect(() => {
    setSelectedGroup(selectedGroup);
  }, [selectedSearchGroup]);

  const onToggleSelectedDestinationGroupsId = (group: BillingGroup) => {
    if (group?.id === selectedGroup?.id) {
      setSelectedGroup(null);
    } else {
      setSelectedGroup(group);
    }
  };

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}>
        <Typography color="inherit" sx={{ ml: 2 }} variant="inherit">
          {t('host:filterByBillingGroup.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1 }}>
          {data?.resources?.map(group => {
            return (
              <Card
                onClick={() => onToggleSelectedDestinationGroupsId(group)}
                key={group.id}
                sx={{
                  boxShadow: theme => {
                    return selectedGroup?.id === group.id
                      ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                      : `0px 0px 0px 1px ${theme.palette.divider}`;
                  },
                  cursor: 'pointer',
                  mb: 2,
                }}>
                <CardContent
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'flex',
                  }}>
                  <Typography color="textPrimary" sx={{ mr: 2 }} variant="h6">
                    {group?.name}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
          <DataStatusIndicator
            isFetching={isLoading}
            error={error?.response?.data?.message}
            dataLength={data?.resources?.length || 0}
          />
        </Box>
        <Pagination
          changePage={changePage}
          page={page}
          changePerPage={changePerPage}
          isFetching={isFetching}
          totalPages={totalPages}
          perPage={perPage}
        />
      </DialogContent>
      <Divider sx={{ mt: 'auto' }} />
      <DialogActions>
        <Button color="primary" onClick={onCancel} variant="text">
          {t('common:button.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={!selectedGroup}
          onClick={() => handleFilterByBillingGroup(selectedGroup as BillingGroup)}
          variant="contained">
          {t('common:button.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
